/**
 * Shared helpers for Plan Total Weeks and Topic Scheduled Week calculation.
 */

export function parseWeekNum(val: string | number | undefined | null): number | null {
  if (val === undefined || val === null || val === '') return null
  if (typeof val === 'number') return isNaN(val) ? null : val
  const match = String(val).match(/\d+/)
  return match ? parseInt(match[0], 10) : null
}

export function getPlanTotalWeeks(plan?: {
  cycleDuration?: string | number | null
  planningCadence?: string | null
  startDate?: string | null
  targetCompletionDate?: string | null
  valueCode?: string | null
}): number {
  if (!plan) return 1

  const code = (plan.valueCode || plan.cycleDuration || '').toString().trim()

  if (code) {
    if (code === '4_WEEKS') return 4
    if (code === '8_WEEKS') return 8
    if (code === '12_WEEKS') return 12
    if (code === '16_WEEKS') return 16
    if (code === '1_MONTH') return 4
    if (code === '2_MONTHS') return 8
    if (code === '3_MONTHS') return 12
    if (code === '6_MONTHS') return 24
    if (code === '1_TERM') return 12
    if (code === '1_SEMESTER') return 16

    if (code.includes('WEEK')) {
      const match = code.match(/\d+/)
      if (match) {
        const weeks = parseInt(match[0], 10)
        if (!isNaN(weeks) && weeks > 0) return weeks
      }
    } else if (code.includes('MONTH')) {
      const match = code.match(/\d+/)
      if (match) {
        const months = parseInt(match[0], 10)
        if (!isNaN(months) && months > 0) return months * 4
      }
    } else {
      const parsed = parseInt(code, 10)
      if (!isNaN(parsed) && parsed > 0) return parsed
    }
  }

  if (plan.startDate && plan.targetCompletionDate) {
    try {
      const start = new Date(plan.startDate).getTime()
      const end = new Date(plan.targetCompletionDate).getTime()
      if (!isNaN(start) && !isNaN(end) && end > start) {
        const diffDays = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)))
        const weeks = Math.ceil(diffDays / 7)
        if (weeks > 0) return weeks
      }
    } catch {}
  }

  return 1
}

export function getScheduledWeekOptions(
  totalWeeks: number,
  currentSavedWeek?: string | number | null,
  valueAsNumber: boolean = false
): Array<{ label: string; value: string }> {
  const maxWeeks = Math.max(1, totalWeeks)
  const options: Array<{ label: string; value: string }> = Array.from({ length: maxWeeks }, (_, idx) => {
    const wNum = idx + 1
    return {
      label: `Week ${wNum}`,
      value: valueAsNumber ? String(wNum) : `Week ${wNum}`,
    }
  })

  if (currentSavedWeek !== undefined && currentSavedWeek !== null && currentSavedWeek !== '') {
    const savedNum = parseWeekNum(currentSavedWeek)
    if (savedNum && savedNum > maxWeeks) {
      const label = `Week ${savedNum}`
      const value = valueAsNumber ? String(savedNum) : label
      if (!options.some((o) => o.value === value || parseWeekNum(o.value) === savedNum)) {
        options.push({ label, value })
      }
    }
  }

  return options
}

export function getDefaultScheduledWeek(
  chapters: Array<{ topics?: Array<{ scheduledWeek?: string | number | null }> }>,
  targetChapterIndex: number,
  totalWeeks: number,
  valueAsNumber: boolean = false
): string {
  let foundWeek: number | null = null

  // 1. Check previous topic in the current chapter
  const targetChapter = chapters[targetChapterIndex]
  if (targetChapter && targetChapter.topics && targetChapter.topics.length > 0) {
    const lastTopicInChap = targetChapter.topics[targetChapter.topics.length - 1]
    if (lastTopicInChap?.scheduledWeek !== undefined && lastTopicInChap?.scheduledWeek !== null && lastTopicInChap?.scheduledWeek !== '') {
      const parsed = parseWeekNum(lastTopicInChap.scheduledWeek)
      if (parsed) foundWeek = parsed
    }
  }

  // 2. If no topic in current chapter, check previous chapter's last topic
  if (foundWeek === null && targetChapterIndex > 0) {
    for (let cIdx = targetChapterIndex - 1; cIdx >= 0; cIdx--) {
      const prevChap = chapters[cIdx]
      if (prevChap && prevChap.topics && prevChap.topics.length > 0) {
        const lastTopic = prevChap.topics[prevChap.topics.length - 1]
        if (lastTopic?.scheduledWeek !== undefined && lastTopic?.scheduledWeek !== null && lastTopic?.scheduledWeek !== '') {
          const parsed = parseWeekNum(lastTopic.scheduledWeek)
          if (parsed) {
            foundWeek = parsed
            break
          }
        }
      }
    }
  }

  // 3. Fallback to Week 1
  const rawWeek = foundWeek !== null ? foundWeek : 1

  // Clamp to totalWeeks (minimum 1, maximum totalWeeks)
  const clamped = Math.max(1, Math.min(rawWeek, Math.max(1, totalWeeks)))

  return valueAsNumber ? String(clamped) : `Week ${clamped}`
}

export function getChapterWeekRangeFromTopics(
  topics?: Array<{ scheduledWeek?: string | number | null }>
): { startWeek: number | null; endWeek: number | null; rangeText: string } {
  if (!topics || topics.length === 0) {
    return { startWeek: null, endWeek: null, rangeText: 'Unscheduled' }
  }

  const weeks: number[] = []
  for (const t of topics) {
    if (t.scheduledWeek !== undefined && t.scheduledWeek !== null && t.scheduledWeek !== '') {
      const parsed = parseWeekNum(t.scheduledWeek)
      if (parsed && parsed > 0) {
        weeks.push(parsed)
      }
    }
  }

  if (weeks.length === 0) {
    return { startWeek: null, endWeek: null, rangeText: 'Unscheduled' }
  }

  const minW = Math.min(...weeks)
  const maxW = Math.max(...weeks)

  const rangeText = minW === maxW ? `Week ${minW}` : `Weeks ${minW}–${maxW}`
  return { startWeek: minW, endWeek: maxW, rangeText }
}
